/** Dates and money for display (CLAUDE.md "Money and dates"): IST, DD-MMM-YYYY, ₹ with Indian grouping. */
import { t, tCount } from './text'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/
const IST_PARTS = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Asia/Kolkata',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
})

type Value = string | Date | null | undefined

function istParts(value: string | Date) {
  const parts = Object.fromEntries(IST_PARTS.formatToParts(new Date(value)).map((p) => [p.type, p.value]))
  return { year: parts.year, month: Number(parts.month), day: parts.day, hour: parts.hour, minute: parts.minute }
}

export function formatDate(value: Value): string {
  if (!value) return ''
  if (typeof value === 'string') {
    const match = DATE_ONLY.exec(value)
    // A plain calendar date has no time zone: show it as written.
    if (match) return `${match[3]}-${MONTHS[Number(match[2]) - 1]}-${match[1]}`
  }
  const p = istParts(value)
  return `${p.day}-${MONTHS[p.month - 1]}-${p.year}`
}

export function formatDateTime(value: Value): string {
  if (!value) return ''
  const p = istParts(value)
  return `${p.day}-${MONTHS[p.month - 1]}-${p.year} ${p.hour}:${p.minute}`
}

export function formatRelative(value: Value, now: Date = new Date()): string {
  if (!value) return ''
  const seconds = Math.max(0, (now.getTime() - new Date(value).getTime()) / 1000)
  if (seconds < 60) return t('core.time.just_now')
  if (seconds < 3600) return tCount('core.time.minutes_ago', Math.floor(seconds / 60))
  if (seconds < 86400) return tCount('core.time.hours_ago', Math.floor(seconds / 3600))
  if (seconds < 7 * 86400) return tCount('core.time.days_ago', Math.floor(seconds / 86400))
  return formatDate(value)
}

function groupIndian(whole: string): string {
  if (whole.length <= 3) return whole
  const last3 = whole.slice(-3)
  const rest = whole.slice(0, -3).replace(/\B(?=(\d{2})+(?!\d))/g, ',')
  return `${rest},${last3}`
}

/** Exact decimal rounding (ROUND_HALF_UP to paise) on the API's string amounts; never via float. */
export function formatInr(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return ''
  const text = String(value).trim()
  const match = /^(-?)(\d*)(?:\.(\d*))?$/.exec(text)
  if (!match) return text
  const negative = match[1] === '-'
  const fraction = (match[3] ?? '').padEnd(3, '0')
  let paise = BigInt(match[2] || '0') * 100n + BigInt(fraction.slice(0, 2))
  if (Number(fraction[2]) >= 5) paise += 1n
  const whole = (paise / 100n).toString()
  const cents = (paise % 100n).toString().padStart(2, '0')
  const sign = negative && paise !== 0n ? '-' : ''
  return `${sign}₹${groupIndian(whole)}.${cents}`
}

/** A calendar month as the API sends it, "2026-11", shown as "Nov-2026". */
export function formatMonth(value: string): string {
  const match = /^(\d{4})-(\d{2})$/.exec(value)
  return match ? `${MONTHS[Number(match[2]) - 1]}-${match[1]}` : value
}

/** A quantity from the API's decimal string, without trailing zeros: "10.000" → "10", "2.500" → "2.5". */
export function formatQuantity(value: string | null | undefined): string {
  if (!value) return ''
  const match = /^(-?)(\d*)(?:\.(\d*))?$/.exec(value.trim())
  if (!match) return value
  const fraction = (match[3] ?? '').replace(/0+$/, '')
  return `${match[1]}${groupIndian(match[2] || '0')}${fraction ? `.${fraction}` : ''}`
}
